import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import { catchError, debounce, filter, map, switchMap } from 'rxjs/operators';
import { AttributeTypesService } from '@emi/shared/data-access';
import * as attributeTypeActions from './attribute-type-actions';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { AttributeTypeState } from './attribute-type-reducer';
import { getAttributeTypeState } from './attribute-type-selectors';
import { AttributeTypeDto, Filters } from '@emi/shared/models';
import { DataExtractionState } from '../extraction/extraction-reducer';
import { getSelectedFilters } from '../extraction/extraction-selectors';
import { createFilterDto } from '@emi/shared/utils';
import * as dataExtractionActions from '../extraction/extraction-actions';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';

@Injectable()
export class AttributeTypeEffects {
    private currentState: AttributeTypeState;
    private currentFilters: Filters;
    private previousFilters: Filters;
    private searchTermState: string;
    private previousSearchTermState: string;
    private includeSearchPhrase: boolean | undefined;

    constructor(
        private actions$: Actions,
        private attributeTypesService: AttributeTypesService,
        private attributeTypeStore: Store<AttributeTypeState>,
        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>
    ) {
        this.attributeTypeStore
            .pipe(select(getAttributeTypeState))
            .subscribe((attributeTypeState) => (this.currentState = attributeTypeState));
        this.skuSearchStore.pipe(select(selectSkuSearch)).subscribe((skuSearchState) => (this.searchTermState = skuSearchState.searchTerm, this.includeSearchPhrase = skuSearchState.includeSearchPhrase));
    }

    getAttributeTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeTypeActions.getAttributeTypes),
            map(() => {
                return attributeTypeActions.isLoadingAttributeTypes();
            })
        )
    );

    getAttributeTypesNextPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeTypeActions.getNextPageAttributeTypes),
            switchMap(() => {
                return this.attributeTypesService
                    .getAttributeTypes(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase? this.searchTermState : undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                            searchText: this.currentState.searchText ?? undefined,
                        })
                    )
                    .pipe(
                        map((data: AttributeTypeDto) => {
                            this.previousFilters = this.currentFilters;
                            const newPage = data.results;

                            return attributeTypeActions.updateAttributeTypesSelectionSuccess({
                                attributeTypes: newPage,
                                metadata: data.metadata,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(attributeTypeActions.getAttributeTypesFailure({ error }));
                        })
                    );
            })
        )
    );

    amendSelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeTypeActions.amendAttributeTypeSelections),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));

                return this.attributeTypesService
                    .getAttributeTypes(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                        })
                    )
                    .pipe(
                        map((attributeTypeResponse) => {
                            const amendedAttributeTypes = this.currentFilters.attributeTypes.filter(
                                (at) => !!attributeTypeResponse.results.find((attributeType) => attributeType.id == at.id)
                            );
                            return dataExtractionActions.addAttributeTypeSelection({ attributeTypes: amendedAttributeTypes });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            if (error.status == 404) {
                                return of(dataExtractionActions.addAttributeTypeSelection({ attributeTypes: [] }));
                            }
                            return of(attributeTypeActions.getAttributeTypesFailure({ error }));
                        })
                    );
            })
        )
    );

    fetchAttributeTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeTypeActions.fetchAttributeTypes),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));
                if (this.currentFilters.attributeTypes.length > 0) {
                    if (this.currentFilters === this.previousFilters && this.searchTermState === this.previousSearchTermState) {
                        return of(
                            attributeTypeActions.getAttributeTypesSuccess({
                                attributeTypes: this.currentState.attributeTypes,
                                metadata: this.currentState.metadata,
                                searchText: this.currentState.searchText,
                            })
                        );
                    }
                }
                return this.attributeTypesService
                    .getAttributeTypes(createFilterDto({ filterState: this.currentFilters, searchTerm: this.includeSearchPhrase? this.searchTermState : undefined }))
                    .pipe(
                        map((data: AttributeTypeDto) => {
                            this.previousFilters = this.currentFilters;
                            this.previousSearchTermState = this.searchTermState;
                            return attributeTypeActions.getAttributeTypesSuccess({
                                attributeTypes: data.results,
                                metadata: data.metadata,
                                searchText: this.currentState.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(attributeTypeActions.getAttributeTypesFailure({ error }));
                        })
                    );
            })
        )
    );

    searchAttributeTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeTypeActions.searchAttributeTypes),
            debounce((action) => timer(action.delay)),
            filter((action) => action.searchText !== this.currentState.searchText),
            concatLatestFrom(() => this.store.select(getSelectedFilters)),
            switchMap(([action, currentFilters]) => {
                return this.attributeTypesService
                    .getAttributeTypes(
                        createFilterDto({
                          filterState: currentFilters,
                          searchText: action.searchText,
                          searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                        })
                    )
                    .pipe(
                        map((data: AttributeTypeDto) => {
                            this.previousFilters = currentFilters;
                            return attributeTypeActions.getAttributeTypesSuccess({
                                attributeTypes: data.results,
                                metadata: data.metadata,
                                searchText: action.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(attributeTypeActions.getAttributeTypesFailure({ error }));
                        })
                    );
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeTypeActions.isLoadingAttributeTypes),
            map(() => attributeTypeActions.fetchAttributeTypes())
        )
    );
}
